import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { PLAN_FEATURE_FALLBACK, PLAN_FEATURE_GROUPS } from '../constants/planFeatures.js';

function formatInterval(interval) {
  return interval === 'year' ? 'year' : 'month';
}

function formatPrice(plan) {
  return `${(plan.price_cents / 100).toFixed(2)} ${plan.currency?.toUpperCase() ?? 'CAD'}`;
}

function groupCatalog(catalog) {
  const groups = {};
  for (const item of catalog) {
    const groupKey = item.group || 'other';
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
  }
  return groups;
}

function PlanFeaturesList({ featureKeys, catalogByKey, compact = false }) {
  const keys = Array.isArray(featureKeys) ? featureKeys : [];
  if (keys.length === 0) {
    return <p className="muted small">No features listed for this plan.</p>;
  }

  const grouped = {};
  for (const key of keys) {
    const item = catalogByKey[key];
    const groupKey = item?.group || 'other';
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    grouped[groupKey].push(item ?? { key, label: key, description: '' });
  }

  return (
    <div className={`plan-features${compact ? ' plan-features-compact' : ''}`}>
      {Object.entries(grouped).map(([groupKey, items]) => (
        <div key={groupKey} className="plan-feature-group">
          {!compact ? (
            <p className="muted small plan-feature-group-title">{PLAN_FEATURE_GROUPS[groupKey] ?? groupKey}</p>
          ) : null}
          <ul className="list-plain plan-feature-list">
            {items.map((item) => (
              <li key={item.key}>
                <span className="plan-feature-label">{item.label}</span>
                {!compact && item.description ? (
                  <span className="muted small plan-feature-desc">{item.description}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function AdminBillingPage() {
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState(PLAN_FEATURE_FALLBACK);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [busyPlanId, setBusyPlanId] = useState(null);

  const catalogByKey = useMemo(() => {
    const map = {};
    for (const item of catalog) {
      map[item.key] = item;
    }
    return map;
  }, [catalog]);

  const loadBilling = useCallback(async () => {
    const [plansData, statusData, catalogData] = await Promise.all([
      apiJson('/api/billing/plans'),
      apiJson('/api/billing/status'),
      apiJson('/api/billing/feature-catalog').catch(() => PLAN_FEATURE_FALLBACK),
    ]);
    setPlans(plansData);
    setStatus(statusData);
    setCatalog(Array.isArray(catalogData) && catalogData.length ? catalogData : PLAN_FEATURE_FALLBACK);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadBilling();
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadBilling]);

  useEffect(() => {
    if (searchParams.get('success')) {
      refreshUser();
    }
  }, [searchParams, refreshUser]);

  const selectPlan = async (planId) => {
    setError('');
    setSuccess('');
    setFieldErrors(undefined);
    setBusyPlanId(planId);
    try {
      const subscribed = status?.subscribed;
      const endpoint = subscribed ? '/api/billing/change-plan' : '/api/billing/checkout';
      const result = await apiJson(endpoint, {
        method: 'POST',
        body: { plan_id: planId },
      });

      if (result.url) {
        window.location.href = result.url;
        return;
      }

      setSuccess(result.message ?? 'Plan updated.');
      await loadBilling();
      await refreshUser();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setBusyPlanId(null);
    }
  };

  const openPortal = async () => {
    setError('');
    try {
      const { url } = await apiJson('/api/billing/portal', { method: 'POST' });
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <p className="muted">Loading billing…</p>;
  }

  const subscribed = status?.subscribed;
  const currentPlanId = status?.subscription?.plan?.id ?? null;
  const currentPlan = status?.subscription?.plan ?? null;

  return (
    <div className="stack billing-page" data-tour="tour-billing-main">
      <div>
        <h2 data-tour="page-title">Subscription</h2>
        <p className="muted">
          {subscribed
            ? 'Your current plan and included capabilities. Switch plans anytime — changes apply immediately or at the next billing cycle via Stripe.'
            : 'Choose a plan to unlock clients, projects, integrations, and team management for your agency.'}
        </p>
      </div>

      {searchParams.get('success') ? (
        <p className="success">Payment received. Your access will update shortly.</p>
      ) : null}
      {searchParams.get('canceled') ? <p className="muted">Checkout was canceled.</p> : null}
      {success ? <p className="success">{success}</p> : null}
      {error ? <p className="err">{error}</p> : null}
      <FieldErrors errors={fieldErrors} />

      {subscribed && currentPlan ? (
        <section className="card-panel current-plan-panel" data-tour="billing-current-plan">
          <div className="current-plan-header">
            <div>
              <p className="plan-badge">Current plan</p>
              <h3 style={{ margin: '0.25rem 0 0' }}>{currentPlan.name}</h3>
              {currentPlan.description ? <p className="muted">{currentPlan.description}</p> : null}
            </div>
            <div className="current-plan-price">
              <strong>{formatPrice(currentPlan)}</strong>
              <span className="muted"> / {formatInterval(currentPlan.interval)}</span>
            </div>
          </div>

          {status?.subscription?.current_period_end ? (
            <p className="muted small">
              {status.subscription.status === 'trialing' ? 'Trial ends' : 'Renews'}:{' '}
              {new Date(status.subscription.current_period_end).toLocaleDateString()}
            </p>
          ) : null}

          <div className="stack" style={{ marginTop: '1rem' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Included in your plan</p>
            <PlanFeaturesList featureKeys={currentPlan.features} catalogByKey={catalogByKey} />
          </div>

          {status?.stripe_configured ? (
            <div className="row-actions" style={{ marginTop: '1rem' }}>
              <button type="button" className="btn-secondary" onClick={openPortal}>
                Manage payment method & invoices
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="stack">
        <h3 style={{ margin: 0 }}>{subscribed ? 'Change plan' : 'Available plans'}</h3>
        <p className="muted small" style={{ margin: 0 }}>
          Compare what each plan includes and pick the one that fits your agency.
        </p>

        <div className="plan-grid plan-grid-wide" data-tour="billing-plans">
          {plans.map((plan) => {
            const isCurrent = subscribed && currentPlanId === plan.id;
            const isBusy = busyPlanId === plan.id;

            return (
              <article
                key={plan.id}
                className={`card-panel plan-card${isCurrent ? ' plan-card-current' : ''}`}
              >
                {isCurrent ? <p className="plan-badge">Current plan</p> : null}
                <h3>{plan.name}</h3>
                <p className="muted">{plan.description}</p>
                <p className="plan-card-price">
                  <strong>{formatPrice(plan)}</strong>
                  <span className="muted"> / {formatInterval(plan.interval)}</span>
                </p>

                <PlanFeaturesList featureKeys={plan.features} catalogByKey={catalogByKey} compact />

                <button
                  type="button"
                  className={isCurrent ? 'btn-secondary' : 'btn-primary'}
                  disabled={isCurrent || isBusy}
                  onClick={() => selectPlan(plan.id)}
                >
                  {isBusy
                    ? 'Processing…'
                    : isCurrent
                      ? 'Current plan'
                      : subscribed
                        ? 'Switch to this plan'
                        : 'Subscribe'}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {!status?.stripe_configured && !subscribed ? (
        <p className="muted small">
          Stripe is not configured. Contact the platform administrator or use a demo subscription in development.
        </p>
      ) : null}
    </div>
  );
}
